/**
 * MONSTER TOWN — THE CREATOR STAGE. MONSTER CREW (src/monster/**).
 *
 * WHY THIS FILE EXISTS.
 *
 * Making your monster is the ownership anchor of the whole game: "this is *them*
 * on screen." It shipped as a couple of button bars laid over the ordinary town,
 * with the monster standing in a road intersection, the house and the lamp posts
 * still behind it and the camera cluster still in the corner. It read as "a
 * monster is standing in the street while some bars appeared" — not as a moment
 * about the kid's own character.
 *
 * So the creator is a TAKEOVER. While it is open:
 *   - the town's ground, buildings and everything the kid has placed are hidden
 *     (visibility only — nothing is unloaded, nothing is saved, nothing is lost);
 *   - a stage is dropped in front of the camera: a plinth, a soft backdrop card
 *     and a halo behind the monster's head;
 *   - the monster is turned to FACE the camera, so its face is symmetrical on
 *     screen instead of being seen three-quarters-on from the side;
 *   - the camera frames the monster itself with `setFitBounds`, so it fills the
 *     screen the same way in portrait and in landscape.
 *
 * Restoring is exact: whatever was visible before comes back, and the camera's
 * previous framing is put back the way it was found.
 */

import * as THREE from 'three'
import { engine } from '../core/engine'
import { world, type SceneNode } from '../core/scene'
import type { IsoCamera } from '../render/camera'
import { ball, box, cylinder, group, inked, planeGeo, quadGeo, softDisc, texturedFlat } from '../render/kit'

/** Base yaw and downward tilt of the iso camera. Mirrors `render/camera.ts`. */
const BASE_YAW = Math.PI / 4
const PITCH = (34 * Math.PI) / 180
/** How far behind the monster the backdrop card sits. */
const CARD_BACK = 9

interface StageState {
  node: SceneNode
  root: THREE.Group
  hidden: { obj: THREE.Object3D; was: boolean }[]
  card: THREE.Object3D
  /** The height the ART was built against — what `aimStage` needs. */
  artHeight: number
  fit: { w: number; h: number; d: number; pad: number } | null
  zoom: number
  /** The one actor that stays: the kid's own monster. Re-asked every frame. */
  star: () => THREE.Object3D | null
  /** What each actor's `visible` was before the takeover, so it goes back. */
  wasVisible: Map<THREE.Object3D, boolean>
}

/**
 * WHO ELSE IS ON THE STAGE: NOBODY.
 *
 * `openStage` hid the ground, the shell and everything placed — but NOT the
 * actors layer, which is where the neighbours live. So whichever monster
 * happened to be strolling past the kid's front door was left standing on the
 * creator screen, mostly hidden behind the kid's own monster. The critic saw the
 * one bit of it that stuck out and read it as anatomy: "the pink party-hat
 * wearable half-buried in the left side of the skull with a pink blob hanging
 * below it — it reads as a growth, not a hat" (`shots/critic-r3/c1-monster-
 * creator.png`, where a second monster is also visible at the bottom-left).
 *
 * Hiding the layer's current children once is not enough: town life keeps
 * spawning, and a monster that arrives while the sheet is open would walk into
 * frame. This runs every frame the stage is up — a handful of `visible` writes
 * over a list of a dozen — and it re-asks for the star each time, because
 * choosing a new body shape REBUILDS the kid's rig into a brand new object.
 */
const SOLO_SYSTEM = 'monster.creatorSolo'

function soloFrame(): void {
  const s = stage
  if (s === null) return
  const star = s.star()
  for (const child of s.node.actors.children) {
    if (child === s.root) continue
    if (!s.wasVisible.has(child)) s.wasVisible.set(child, child.visible)
    child.visible = star !== null && child === star ? s.wasVisible.get(child) === true : false
  }
}

/** Put every actor back exactly as it was found, star included. */
function soloRestore(s: StageState): void {
  for (const [obj, was] of s.wasVisible) obj.visible = was
  s.wasVisible.clear()
}

/**
 * Breathing room around the framed box, before the dock is taken off. Tuned
 * against the measured canvas box of the TALLEST body shape (`__mtWalk
 * .screenBox`), not by eye: it leaves ~38 px of clear canvas above the horns and
 * below the feet on a 1180x820 iPad, at every body shape, in both orientations.
 */
const FIT_PAD = 1.58

/** Apply the zoom that fits `frameH` into the part of the canvas the dock leaves. */
function frameInto(camera: IsoCamera, frameH: number, reserve: number): void {
  const h = Math.max(0.5, frameH)
  const free = Math.max(0.4, 1 - Math.max(0, Math.min(0.6, reserve)))
  camera.setFitBounds({ w: h * 0.34, h, d: h * 0.34, pad: FIT_PAD / free })
  camera.setZoomIndex(2, true)
}

let stage: StageState | null = null

/** Direction from the monster TOWARDS the camera, for a camera rotation. */
export function cameraYaw(rot: number): number {
  return BASE_YAW + (rot * Math.PI) / 2
}

/** The camera's downward tilt, for anyone converting screen offsets to world. */
export const CAMERA_PITCH = PITCH

export function stageIsOpen(): boolean {
  return stage !== null
}

/**
 * Build the stage around `at`, sized off the monster's height so every body
 * shape gets the same composition.
 */
function buildStage(at: { x: number; z: number }, h: number): { root: THREE.Group; card: THREE.Object3D } {
  const root = group('creatorStage')
  root.position.set(at.x, 0, at.z)

  /**
   * A LOW MAT, NOT A PEDESTAL. The first version was a plinth of radius 0.95h —
   * nearly twice the framed box — which filled the screen and left the monster
   * looking like a toy on a dinner plate. It stands on a rug the size of its own
   * feet instead.
   */
  /**
   * THE MAT'S TOP SURFACE IS y = 0, WHICH IS WHERE THE FEET ARE.
   *
   * It used to stack upward from the ground: a 0.035h base with a 0.02h disc on
   * top of it, so the walking surface was 0.055h — at creator scale a quarter of
   * a world unit — ABOVE the plane the monster stands on. The monster's feet are
   * 0.41 units tall there, so 60% of each foot was inside the mat along with the
   * whole underside of its ink outline. The critic read the symptom exactly:
   * "the feet have no ink line and merge into the plinth". They had one; it was
   * under the rug.
   *
   * So the mat is SUNK by its own thickness. Same low two-tier rug, same
   * silhouette, but the kid's monster now stands ON it instead of IN it. The
   * town has no equivalent bug — grass is at y = 0 there.
   */
  const matH = h * 0.035
  const topH = h * 0.02
  const base = inked(cylinder(h * 0.46, matH, { color: 'uiPanelAlt', seg: 40 }))
  base.position.y = -(matH + topH)
  root.add(base)
  const top = inked(cylinder(h * 0.36, topH, { color: 'uiPanel', seg: 40 }))
  top.position.y = -topH
  root.add(top)

  /**
   * A CONTACT SHADOW, so the monster is STANDING on the mat.
   *
   * Without it the critic read the whole screen as "the monster floats over an
   * abstract blob" — and that was fair: a creature with no shadow on a disc with
   * no shading has nothing tying it to the ground. A soft gradient quad lying on
   * the mat costs one draw call and does the entire job.
   */
  const shadow = new THREE.Mesh(
    planeGeo(h * 0.62, h * 0.62),
    texturedFlat('softdisc:shadow', softDisc(0.18), { color: 'shadow', opacity: 0.24 }),
  )
  shadow.position.y = h * 0.002
  shadow.renderOrder = 1
  root.add(shadow)

  /**
   * THE BACKDROP IS SCREEN-ALIGNED, not merely turned to the yaw.
   *
   * An upright card placed behind the monster does NOT land behind it on screen:
   * under a 34-degree camera, everything further away is drawn higher, so the
   * first version floated off the top of the frame as a cream band with the
   * monster on bare sky. Aimed square at the lens (`aimStage`), the card's
   * centre projects exactly onto the monster's, at any rotation and any zoom.
   */
  const card = group('creatorCard')
  const panel = inked(box(h * 6, h * 4.6, h * 0.1, { color: 'wallCream' }))
  card.add(panel)

  /**
   * THE HALO IS A GRADIENT, NOT A FLATTENED BALL.
   *
   * It was two spheres squashed to 5% depth, which is a polygon: at 20 segments
   * the critic read them exactly as they are — "two concentric flat polygons,
   * visibly faceted decagons rather than circles". A sphere used as a disc will
   * always show its segment count, and raising the count only makes it a more
   * expensive polygon. A radial-gradient quad has no edge at all, dissolves into
   * the card instead of ending on a hard rim, and is one draw call each.
   */
  const halo = new THREE.Mesh(
    quadGeo(h * 2.5, h * 2.5),
    texturedFlat('softdisc:halo', softDisc(0.42), { color: 'wallBlue', opacity: 0.85 }),
  )
  halo.position.set(0, h * 0.06, h * 0.07)
  card.add(halo)

  const halo2 = new THREE.Mesh(
    quadGeo(h * 1.9, h * 1.9),
    texturedFlat('softdisc:halo2', softDisc(0.34), { color: 'uiPanel', opacity: 0.95 }),
  )
  halo2.position.set(0, h * 0.06, h * 0.09)
  card.add(halo2)

  // Little stars, so the card is a party backdrop rather than a wall.
  const stars: [number, number, number][] = [
    [-1.02, 0.62, 0.1],
    [1.06, 0.7, 0.12],
    [-1.2, -0.15, 0.08],
    [1.24, -0.22, 0.09],
    [-0.6, 1.0, 0.07],
    [0.66, 1.04, 0.085],
  ]
  for (const [sx, sy, sr] of stars) {
    const s = ball(0.5, { color: 'uiCoin', seg: 8 })
    s.scale.setScalar(h * sr)
    s.position.set(h * sx, h * sy, h * 0.08)
    card.add(s)
  }
  /**
   * THE BACKDROP TAKES NO SHADOWS.
   *
   * It is a card nine units behind the monster, so the directional light drops a
   * big soft monster-shaped smudge onto it — a grey blob hanging in the sky
   * behind the creature with nothing casting it, since the thing that cast it is
   * lit from the front. It was invisible before only because the old framing hid
   * that part of the screen behind the option dock. Backdrops are backdrop.
   */
  card.traverse((o) => {
    const m = o as THREE.Mesh
    m.receiveShadow = false
    m.castShadow = false
  })

  root.add(card)
  return { root, card }
}

/** Square the backdrop to the lens. Called on open and on every rotate. */
export function aimStage(rot: number, h: number): void {
  if (stage === null) return
  const yaw = cameraYaw(rot)
  const cp = Math.cos(PITCH)
  const sp = Math.sin(PITCH)
  // Unit vector from the monster towards the camera.
  const toCam = new THREE.Vector3(Math.sin(yaw) * cp, sp, Math.cos(yaw) * cp)
  // Straight back along the view axis (which cannot move it on screen), lifted
  // to the monster's middle so the halo lands behind its head.
  const card = stage.card
  card.position.copy(toCam).multiplyScalar(-CARD_BACK)
  card.position.y += h * 0.5
  /**
   * Turn, then tilt. `lookAt` is NOT usable here: the card is a child of a
   * translated stage root, `lookAt` takes a WORLD target, and the parent's
   * matrix is not up to date on the frame the stage is built — which threw the
   * card off by the whole town offset and left the backdrop hanging in the sky
   * with the monster on bare blue.
   */
  card.rotation.set(0, 0, 0)
  card.rotateY(yaw)
  card.rotateX(-PITCH)
}

/**
 * Take the screen over. Idempotent: calling it twice is a no-op, so a repeated
 * phase event can never double-hide the town.
 */
export function openStage(
  camera: IsoCamera,
  at: { x: number; z: number },
  height: number,
  /**
   * Fraction of the CANVAS height the option dock covers. The camera has to
   * frame the monster into what is LEFT, not into the whole screen: the critic
   * found the hands cut off by the dock and everything below the chest hidden,
   * on the one screen where the body shape is the thing being chosen. Measured
   * off the real island in `creator.ts`, never assumed.
   */
  reserve = 0,
  /**
   * The box the CAMERA frames, as opposed to the box the ART is built against.
   * They differ on purpose: the stage furniture is sized to a fixed reference so
   * the plinth does not resize under the kid's finger, but the zoom has to clear
   * the TALLEST body shape or picking "tall" crops its own horns off.
   */
  frameHeight = height,
  /**
   * The kid's own monster — the ONE actor that stays on the takeover. A getter,
   * not the object, because picking a new body shape rebuilds the rig.
   */
  star: () => THREE.Object3D | null = () => null,
): void {
  if (stage !== null) return
  const id = world.activeSceneId
  if (id === null) return
  const node = world.get(id)
  if (node === undefined) return

  const h = Math.max(0.5, height)
  const hidden: { obj: THREE.Object3D; was: boolean }[] = []
  for (const layer of [node.ground, node.statics, node.placeables]) {
    hidden.push({ obj: layer, was: layer.visible })
    layer.visible = false
  }

  const built = buildStage(at, h)
  node.actors.add(built.root)

  stage = {
    node,
    root: built.root,
    hidden,
    card: built.card,
    artHeight: h,
    fit: camera.fitBounds,
    zoom: camera.zoomLevel,
    star,
    wasVisible: new Map(),
  }
  // Order 31: monster band, after the player has moved its own rig this frame.
  engine.addSystem({ id: SOLO_SYSTEM, order: 31, render: soloFrame })
  soloFrame()
  aimStage(camera.rotation, h)

  /**
   * Frame the MONSTER, not the town — and only into the part of the canvas the
   * option dock leaves. Aspect-aware, so portrait and landscape both put it on
   * the screen at the same size. The box is the monster's own footprint, NOT a
   * cube of its height: `setFitBounds` adds width and depth together for the iso
   * span, so a `h`-deep box zooms out until the creature is a third of the
   * screen.
   */
  frameInto(camera, frameHeight, reserve)
}

/**
 * Re-apply the framing for a new dock size or a new aspect ratio, without
 * rebuilding the stage. `openStage` is idempotent by design (a repeated phase
 * event must never double-hide the town), so a resize needs its own door in.
 */
export function reframeStage(camera: IsoCamera, frameHeight: number, reserve: number): void {
  if (stage === null) return
  frameInto(camera, frameHeight, reserve)
  aimStage(camera.rotation, stage.artHeight)
}

/** Give the town back exactly as it was found. */
export function closeStage(camera: IsoCamera): void {
  const s = stage
  if (s === null) return
  stage = null
  engine.removeSystem(SOLO_SYSTEM)
  soloRestore(s)
  for (const { obj, was } of s.hidden) obj.visible = was
  s.root.parent?.remove(s.root)
  camera.setFitBounds(s.fit)
  camera.setZoomIndex(s.zoom, true)
}
